import { useState, useMemo } from 'react';
import { Box, Button, TextInput, Text, Transition, Skeleton, createStyles } from '@mantine/core';
import { IconPlus, IconSearch, IconLayoutSidebarLeftCollapse, IconShare } from '@tabler/icons';
import { useDiscoveryStore } from '../../stores/useDiscoveryStore';
import type { DiscoveryStore } from '../../stores/useDiscoveryStore';
import { useDiscoverySessionUrl } from '../../hooks/useDiscoverySessionUrl';
import { useDiscoverySessions, useActiveDiscoverySession } from '../../hooks/useDiscoveryAgent';
import type { DiscoverySession } from '../../api/discoveryAgent';

const useStyles = createStyles(() => ({
  settingTitleText: {
    backgroundImage: 'linear-gradient(to right, #868E96, #ADB5BD, #5C5CEB, #ADB5BD, #868E96)',
    backgroundSize: '400% 100%',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    animation: 'sidebarTitleAnimation 8s linear infinite',
    '@keyframes sidebarTitleAnimation': {
      '0%': { backgroundPosition: '0% 50%' },
      '100%': { backgroundPosition: '-800% 50%' },
    },
  },
}));

const DEFAULT_SESSION_NAME = 'Untitled';

function hasSessionName(session: DiscoverySession): boolean {
  return !!session.sessionName?.trim();
}

function getSessionTitle(session: DiscoverySession): string {
  return session.sessionName?.trim() || DEFAULT_SESSION_NAME;
}

interface ExploreSessionListProps {
  isCollapsed?: boolean;
  onCollapse?: () => void;
  onExpand?: () => void;
}

export const ExploreSessionList = ({ isCollapsed = false, onCollapse, onExpand }: ExploreSessionListProps) => {
  const { classes } = useStyles();
  const { sessions, isLoading: isSessionsLoading } = useDiscoverySessions({ enabled: true });
  const { session: activeSession, sessionId, isSessionFromDirectNavigation } = useActiveDiscoverySession();
  const { setSessionId } = useDiscoverySessionUrl();
  const reset = useDiscoveryStore((s) => s.reset);
  const isMessageSending = useDiscoveryStore((s: DiscoveryStore) => s.isMessageSending);
  const messageSendingSessionId = useDiscoveryStore((s: DiscoveryStore) => s.messageSendingSessionId);

  const [searchQuery, setSearchQuery] = useState('');

  const filteredSessions = useMemo(() => {
    if (!searchQuery.trim()) return sessions;
    const q = searchQuery.toLowerCase().trim();
    return sessions.filter((s) => getSessionTitle(s).toLowerCase().includes(q));
  }, [sessions, searchQuery]);

  const displaySessions = useMemo(() => {
    if (!isSessionFromDirectNavigation || !activeSession) return filteredSessions;
    return [activeSession, ...filteredSessions];
  }, [filteredSessions, activeSession, isSessionFromDirectNavigation]);

  const handleNewChat = () => {
    setSessionId(null);
    reset();
  };

  const handleSelectSession = (session: DiscoverySession) => {
    setSessionId(session.id ?? session.sessionId);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        height: '100%',
        minHeight: 0,
        backgroundColor: '#f8f8fb',
        borderRight: '1px solid #D8D8DB',
        transition: 'background-color 0.2s ease',
      }}
    >
      <Box p="md" sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Transition mounted={!isCollapsed} transition="fade" duration={220} timingFunction="ease">
          {(styles) => (
            <Box
              style={styles}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
              }}
            >
              <Box
                component="button"
                type="button"
                onClick={onCollapse}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  cursor: 'pointer',
                  marginBottom: 10,
                  border: 'none',
                  background: 'none',
                  padding: 0,
                  width: '100%',
                }}
                aria-label="Collapse sidebar"
              >
                <IconLayoutSidebarLeftCollapse size={28} stroke={1} color={'#495057'} />
              </Box>
              <Box sx={{ marginBottom: 20, flexShrink: 0 }}>
                <Button
                  leftIcon={<IconPlus size={16} />}
                  onClick={handleNewChat}
                  variant="subtle"
                  size="sm"
                  fullWidth
                  sx={{
                    width: '100%',
                    color: '#212529',
                    fontWeight: 600,
                    padding: '8px 12px',
                    borderRadius: 8,
                    justifyContent: 'left',
                    transition: 'background-color 0.15s ease',
                    '& .mantine-Button-inner': {
                      justifyContent: 'flex-start',
                    },
                    '&:hover': {
                      backgroundColor: '#EAEBEE',
                    },
                  }}
                >
                      New chat
                </Button>
                <TextInput
                  placeholder="Search"
                  size="sm"
                  icon={<IconSearch size={16} color="#868E96" />}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.currentTarget.value)}
                  sx={{
                    width: '100%',
                    '& .mantine-TextInput-input': {
                      backgroundColor: searchQuery.trim() ? '#EAEBEE' : 'transparent',
                      border: 0,
                      borderRadius: 8,
                      transition: 'background-color 0.15s ease',
                    },
                    '&:hover .mantine-TextInput-input': {
                      backgroundColor: '#EAEBEE',
                    },
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Text size="sm" fw={500} c="#868E96" mb={2} ml={4}>
                      Recents
                </Text>
                <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }} pb="md">
                  {isSessionsLoading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Skeleton
                          key={i}
                          height={32}
                          radius={8}
                          sx={{ opacity: 1 - i * 0.15 }}
                        />
                      ))}
                    </Box>
                  ) : displaySessions.length === 0 ? (
                    <Text size="sm" c="dimmed">
                      {searchQuery.trim()
                        ? `No sessions found for "${searchQuery.trim()}"`
                        : 'Recent and active threads will appear here'}
                    </Text>
                  ) : (
                    displaySessions.map((session) => {
                      const isSelected = sessionId === (session.id ?? session.sessionId);
                      const sessionKey = session.id ?? session.sessionId;
                      const isAwaitingTitle =
                        isSelected &&
                        isMessageSending &&
                        messageSendingSessionId !== null &&
                        sessionKey === messageSendingSessionId &&
                        !hasSessionName(session);
                      return (
                        <Box
                          key={session.sessionId ?? session.id ?? String(session.createdAt)}
                          onClick={() => handleSelectSession(session)}
                          sx={{
                            cursor: 'pointer',
                            padding: '6px 8px',
                            borderRadius: 8,
                            overflow: 'hidden',
                            backgroundColor: isSelected ? 'rgba(92, 92, 235, 0.1)' : 'transparent',
                            transition: 'background-color 0.15s ease',
                            '&:hover': {
                              backgroundColor: isSelected ? 'rgba(92, 92, 235, 0.14)' : '#F3F3F3',
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              overflow: 'hidden',
                            }}
                          >
                            <Text
                              fs={14}
                              fw={500}
                              c={isAwaitingTitle ? '#868E96' : '#000000'}
                              className={isAwaitingTitle ? classes.settingTitleText : undefined}
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {isAwaitingTitle ? 'Naming this chat...' : getSessionTitle(session)}
                            </Text>
                            {session.isShared === true && (
                              <IconShare
                                size={14}
                                color="#868E96"
                                style={{ flexShrink: 0 }}
                                aria-label="Shared"
                              />
                            )}
                          </Box>
                        </Box>
                      );
                    })
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </Transition>
      </Box>
    </Box>
  );
}

export default ExploreSessionList;